/**
 * PerfEditRequestsInbox — Iter 400.15
 *
 * CRM-only view of every PENDING (and, on demand, historic) portal
 * request to edit a saved performance reading. Owner/Admin/Manager
 * can Approve → append a new immutable row to `perf_readings`, or
 * Reject → mark the request rejected. Old row is preserved for audit
 * in both cases.
 */
import { useEffect, useState, useCallback } from "react";
import {
  Loader2,
  CheckCircle2,
  XCircle,
  Image as ImageIcon,
  ArrowRight,
  Clock,
  Filter,
} from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { toast } from "../ui/sonner";

export default function PerfEditRequestsInbox({ customerId, autoOpenReqId }) {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [openPreviewUrl, setOpenPreviewUrl] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const qs = new URLSearchParams({ status });
      if (customerId) qs.set("customer_id", customerId);
      const { data } = await api.get(
        `/perf/readings/edit-requests?${qs.toString()}`,
      );
      setRows(data.rows || []);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [customerId, status]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (autoOpenReqId && rows.length) {
      const found = rows.find((r) => r.id === autoOpenReqId);
      if (found?.verification_photo_url) setOpenPreviewUrl(found.verification_photo_url);
    }
  }, [autoOpenReqId, rows]);

  const decide = async (req, approve) => {
    setBusyId(req.id);
    try {
      await api.post(`/perf/readings/edit-request/${req.id}/decide`, {
        approve,
      });
      toast.success(approve ? "Edit approved · new row appended" : "Edit rejected");
      setRows((rs) => rs.filter((r) => r.id !== req.id));
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="perf-edit-requests-inbox">
      <div className="flex items-center gap-2">
        <Filter className="w-3.5 h-3.5 text-[#647087]" />
        {["pending", "approved", "rejected"].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatus(s)}
            className={`px-2.5 py-1 rounded text-[11px] uppercase tracking-wider transition ${
              status === s
                ? "bg-[#22d3ee]/20 text-[#22d3ee] border border-[#22d3ee]/40"
                : "text-[#a0aabf] border border-white/10 hover:text-white"
            }`}
            data-testid={`perf-edit-requests-filter-${s}`}
          >
            {s}
          </button>
        ))}
        <div className="ml-auto text-[11px] text-[#647087]">
          {loading ? "Loading…" : `${rows.length} request${rows.length === 1 ? "" : "s"}`}
        </div>
      </div>

      {err && (
        <div className="hv-card border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200">
          {err}
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div
          className="hv-card p-8 text-center text-sm text-[#647087] flex flex-col items-center gap-2"
          data-testid="perf-edit-requests-empty"
        >
          <Clock className="w-6 h-6 text-emerald-400/60" />
          {status === "pending"
            ? "No pending edit requests. You're all caught up."
            : `No ${status} edit requests.`}
        </div>
      )}

      <div className="space-y-2">
        {rows.map((r) => (
          <div
            key={r.id}
            className="hv-card p-3 flex flex-col sm:flex-row gap-3"
            data-testid={`perf-edit-request-${r.id}`}
          >
            {r.verification_photo_url ? (
              <button
                type="button"
                onClick={() => setOpenPreviewUrl(r.verification_photo_url)}
                className="shrink-0 w-24 h-24 rounded overflow-hidden border border-white/10 bg-white/[0.02] hover:border-[#22d3ee] transition"
                data-testid={`perf-edit-request-photo-${r.id}`}
                title="Click to enlarge"
              >
                <img src={r.verification_photo_url} alt="proof" className="w-full h-full object-cover" />
              </button>
            ) : (
              <div className="shrink-0 w-24 h-24 rounded border border-white/10 bg-white/[0.02] flex items-center justify-center text-[#647087]">
                <ImageIcon className="w-6 h-6" />
              </div>
            )}
            <div className="flex-1 min-w-0 space-y-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold">
                  {r.equipment_name || "Equipment"} · {r.parameter_name || "?"}
                </span>
                {r.parameter_unit && (
                  <span className="text-[10px] text-[#647087]">{r.parameter_unit}</span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap text-[12px] font-mono">
                <span className="text-rose-300 line-through">{r.old_value ?? "—"}</span>
                <ArrowRight className="w-3.5 h-3.5 text-[#647087]" />
                <span className="text-emerald-300 font-bold">{r.new_value ?? "—"}</span>
                {r.parameter_unit && (
                  <span className="text-[10px] text-[#647087]">{r.parameter_unit}</span>
                )}
              </div>
              <div className="text-[11px] text-[#a0aabf]">
                <strong>{r.requested_by_name || "?"}</strong> ·{" "}
                {(r.requested_at || "").slice(0, 16).replace("T", " ")}
                {r.site_name && (
                  <span className="text-[#647087]"> · {r.site_name}</span>
                )}
              </div>
              {r.reason && (
                <div className="text-[11px] text-[#a0aabf] bg-white/[0.02] border border-white/5 rounded p-1.5">
                  <span className="text-[9px] uppercase tracking-wider text-[#647087]">Reason</span>
                  <div className="mt-0.5">&ldquo;{r.reason}&rdquo;</div>
                </div>
              )}
              {r.new_notes && r.new_notes !== r.old_notes && (
                <div className="text-[11px] text-[#647087]">
                  Notes: <span className="text-[#a0aabf]">{r.new_notes}</span>
                </div>
              )}
              {status === "pending" && (
                <div className="flex items-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => decide(r, true)}
                    disabled={busyId === r.id}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-emerald-400/40 bg-emerald-500/10 text-[11px] text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-50"
                    data-testid={`perf-edit-request-approve-${r.id}`}
                  >
                    {busyId === r.id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-3 h-3" />
                    )}
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => decide(r, false)}
                    disabled={busyId === r.id}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-rose-400/40 bg-rose-500/10 text-[11px] text-rose-300 hover:bg-rose-500/20 disabled:opacity-50"
                    data-testid={`perf-edit-request-reject-${r.id}`}
                  >
                    <XCircle className="w-3 h-3" />
                    Reject
                  </button>
                </div>
              )}
              {status !== "pending" && r.decided_by_name && (
                <div className="text-[11px] text-[#647087] pt-1">
                  {status === "approved" ? "Approved" : "Rejected"} by{" "}
                  <strong>{r.decided_by_name}</strong> ·{" "}
                  {(r.decided_at || "").slice(0, 16).replace("T", " ")}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {openPreviewUrl && (
        <div
          className="fixed inset-0 z-[9500] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setOpenPreviewUrl(null)}
          data-testid="perf-edit-request-photo-preview"
        >
          <img
            src={openPreviewUrl}
            alt="proof enlarged"
            className="max-w-full max-h-full rounded shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
